import { z } from 'zod';

/**
 * Calculator types — Zod schemas + inferred TS types for the
 * standalone quote calculator (Slice 8 PR#1, Entregable A).
 *
 * The calculator is a FE-only pricing sandbox: it never persists.
 * Its math lives in `../lib/calculator-math.ts` (the single source
 * of truth on the FE) and is mirrored byte-for-byte by the optional
 * BE parity endpoint `POST /api/v1/simulate/quote`
 * (`apps/api/src/routes/simulate.ts`). Both use decimal.js with the
 * same `TAX_RATE` constant so numbers match.
 *
 * ROLE-GATING (design obs-443 §E.3): cost-derived outputs
 * (`margin`, `commission`) are gated by `role !== 'WAREHOUSE'`,
 * matching the `ProductResource.defaultCost` precedent from Slice 1.
 * WAREHOUSE never sees cost-derived numbers (cost-leak prevention).
 *
 * NOTE — web uses Zod 3 (`^3.25.76`), not Zod 4. Schemas below use
 * the Zod 3 API.
 */

// ---------------------------------------------------------------------------
// Product — lightweight shape the calculator needs (subset of the
// catalog Product). `defaultCost` is optional because the products
// API strips it for WAREHOUSE (field-gating).
// ---------------------------------------------------------------------------
export const calculatorProductSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  sku: z.string().min(1),
  /** Decimal-as-string, currency precision. */
  defaultSalePrice: z.string(),
  /** PRESENT for ADMIN/SELLER, ABSENT for WAREHOUSE. */
  defaultCost: z.string().optional(),
});
export type CalculatorProduct = z.infer<typeof calculatorProductSchema>;

// ---------------------------------------------------------------------------
// Currency — lightweight shape (subset of catalog Currency).
// ---------------------------------------------------------------------------
export const calculatorCurrencySchema = z.object({
  id: z.string().min(1),
  code: z.string().length(3),
  symbol: z.string().min(1).max(8),
  decimalPlaces: z.number().int().min(0).max(12),
});
export type CalculatorCurrency = z.infer<typeof calculatorCurrencySchema>;

// ---------------------------------------------------------------------------
// QuoteInput — the user-editable inputs.
//   - quantity / discountPercent / exchangeRate are strings so the
//     numeric inputs can hold transient empty/partial values without
//     coercing to NaN mid-edit (same pattern as QuotationForm).
// ---------------------------------------------------------------------------
export const quoteInputSchema = z.object({
  productId: z.string(),
  quantity: z.string(),
  discountPercent: z.string(),
  currencyId: z.string(),
  exchangeRate: z.string(),
  includeTax: z.boolean(),
  /** Financing term in months — lookup key into `FINANCING_TERMS`. */
  termMonths: z.number().int().positive(),
});
export type QuoteInput = z.infer<typeof quoteInputSchema>;

// ---------------------------------------------------------------------------
// CommissionProfile — commission expressed as both a % and a resolved
// amount for the currently entered figures.
// ---------------------------------------------------------------------------
export const commissionProfileSchema = z.object({
  percentage: z.number(),
  amount: z.number(),
});
export type CommissionProfile = z.infer<typeof commissionProfileSchema>;

// ---------------------------------------------------------------------------
// QuoteOutput — derived, never edited directly. All money values are
// numbers rounded to the working precision; `margin`/`commission` are
// null when hidden by role gating or when cost data is unavailable.
// ---------------------------------------------------------------------------
export const quoteOutputSchema = z.object({
  subtotal: z.number(),
  discountAmount: z.number(),
  taxableBase: z.number(),
  taxAmount: z.number(),
  total: z.number(),
  totalInBaseCurrency: z.number(),
  /** (taxableBase - unitCost*qty) / taxableBase * 100 — role-gated. */
  margin: z.number().nullable(),
  /**
   * French amortization on `total` using the continuous `tasaMensual`
   * rate curve (linear between 3.0% @ 3m and 5.5% @ 60m, extrapolated
   * beyond). Returned for every role (no cost information leaked).
   * `null` when the principal is non-positive or the term is omitted.
   */
  installment: z.number().nullable(),
});
export type QuoteOutput = z.infer<typeof quoteOutputSchema>;

// ---------------------------------------------------------------------------
// CalculatorState — the full persisted+derived snapshot.
//   `tenantId` scopes localStorage so switching tenants resets state.
// ---------------------------------------------------------------------------
export const calculatorStateSchema = z.object({
  input: quoteInputSchema,
  output: quoteOutputSchema,
  lastUpdated: z.number(),
});
export type CalculatorState = z.infer<typeof calculatorStateSchema>;

// ---------------------------------------------------------------------------
// Persisted envelope — what we actually put in localStorage. We only
// persist the input (output is always re-derived) plus the tenant
// scope and a schema version.
//
// Version history: v1 was the Slice 8 PR#1 baseline (no installment).
// v2 adds `termMonths` to `quoteInputSchema`; the reader in
// `calculator-persistence.ts` tolerates v1 payloads and backfills.
// ---------------------------------------------------------------------------
export const persistedCalculatorSchema = z.object({
  version: z.literal(2),
  tenantId: z.string(),
  input: quoteInputSchema,
});
export type PersistedCalculator = z.infer<typeof persistedCalculatorSchema>;

/** Default (empty) calculator input. */
export const EMPTY_QUOTE_INPUT: QuoteInput = {
  productId: '',
  quantity: '1',
  discountPercent: '0',
  currencyId: '',
  exchangeRate: '1',
  includeTax: true,
  termMonths: 12,
};
